import type { TranslationDictionaryType } from "@shared/types/i18n.interface";
import type { AssertMatchingPlaceholders } from "@shared/types/translation-key.types";

import type { en } from "./en";

export const es = {
  "common.toggle": "Alternar",
  "common.retry": "Reintentar",
  "common.save": "Guardar",
  "common.saveChanges": "Guardar cambios",
  "common.saving": "Guardando...",
  "common.cancel": "Cancelar",
  "common.edit": "Editar",
  "common.delete": "Eliminar",
  "common.close": "Cerrar",
  "common.dismiss": "Descartar",
  "common.back": "Volver",
  "common.next": "Siguiente",
  "common.submit": "Enviar",
  "common.loading": "Cargando...",
  "common.search": "Buscar",
  "common.actions": "Acciones",
  "common.view": "Ver",
  "common.showRawJson": "Mostrar JSON crudo",

  "status.enabled": "Habilitado",
  "status.disabled": "Deshabilitado",
  "status.active": "Activo",
  "status.inactive": "Inactivo",
  "status.saved": "Guardado",
  "status.default": "Predeterminado",

  "common.featureNotImplemented.title": "Muy pronto",
  "common.featureNotImplemented.description":
    "Esta funcionalidad está en desarrollo. Próximamente disponible.",

  "common.unauthorized.title": "Acceso denegado",
  "common.unauthorized.message":
    "No tienes permiso para acceder a esta página.",

  "errors.generic": "Ocurrió un error. Por favor, intenta de nuevo.",
  "errors.network": "No se pudo conectar. Por favor, verifica tu conexión.",
  "errors.notFound": "No se encontró el {{resource}} solicitado.",
  "errors.serverError": "Error del servidor. Por favor, intenta más tarde.",
  "errors.validationFailed": "Por favor, revisa los errores del formulario.",
  "errors.unknown": "Error desconocido",

  "auth.otp.title.authenticating": "Autenticando",
  "auth.otp.title.failed": "Error de autenticación",
  "auth.otp.verifying": "Verificando tu autenticación...",
  "auth.otp.redirecting": "Autenticación exitosa. Redirigiendo...",
  "auth.otp.error.noToken": "No se proporcionó un token de autenticación.",
  "auth.otp.error.invalid":
    "Este enlace de autenticación es inválido o ha expirado. Por favor, solicita un nuevo enlace para continuar.",
  "auth.otp.error.generic":
    "La autenticación falló. Por favor, intenta de nuevo.",
  "auth.otp.expired.title": "Solicita un nuevo enlace",
  "auth.otp.expired.message":
    "Por tu seguridad, vuelve al chat y solicítalo de nuevo.",

  "magicLink.title.processing": "Procesando tu enlace...",
  "magicLink.error.noId": "No se proporcionó un enlace.",
  "magicLink.error.invalid":
    "Este enlace es inválido o ha expirado. Por favor, solicita un nuevo enlace para continuar.",
  "magicLink.error.generic":
    "No se pudo procesar el enlace. Por favor, intenta de nuevo.",

  "login.title": "Bienvenido de nuevo",
  "login.subtitle": "Inicia sesión para continuar en Planta.",
  "login.email.label": "Email",
  "login.email.placeholder": "Introduce tu Email",
  "login.password.label": "Contraseña",
  "login.password.placeholder": "Introduce tu Contraseña",
  "login.submit": "Iniciar sesión",
  "login.submitting": "Iniciando sesión...",
  "login.orSeparator": "o",
  "login.error.invalidCredentials":
    "Credenciales inválidas. Por favor verifica tu Email y Contraseña.",
  "login.error.serverError": "Error del servidor. Por favor intenta más tarde.",
  "login.error.networkError":
    "Error de red. Por favor verifica tu conexión e intenta de nuevo.",
  "login.error.failed":
    "Error al iniciar sesión (error {{code}}). Por favor intenta de nuevo.",
  "login.error.generic":
    "Ocurrió un error inesperado. Por favor intenta de nuevo.",
  "login.signInWithGoogle": "Iniciar sesión con Google",

  "auth.oauth.title.completing": "Completando el inicio de sesión...",
  "auth.oauth.error.title": "Error al iniciar sesión",
  "auth.oauth.error.missingParams":
    "Al enlace de inicio de sesión le falta información requerida. Por favor, intenta iniciar sesión de nuevo.",
  "auth.oauth.error.invalidState":
    "Este enlace de inicio de sesión es inválido o ha expirado. Por favor, intenta iniciar sesión de nuevo.",
  "auth.oauth.error.exchangeFailed":
    "No pudimos completar el inicio de sesión con el proveedor de identidad. Por favor, intenta de nuevo.",
  "auth.oauth.error.emailNotVerified":
    "Tu correo electrónico no está verificado con el proveedor de identidad. Por favor, verifícalo e intenta de nuevo.",
  "auth.oauth.error.unsupportedSource":
    "Este método de inicio de sesión no es compatible.",
  "auth.oauth.error.authMethodMismatch":
    "Esta cuenta fue creada con otro método de inicio de sesión. Por favor, utiliza el método original para iniciar sesión.",
  "auth.oauth.error.signupNotAllowed":
    "El registro mediante este proveedor de identidad no está disponible para este correo electrónico.",
  "auth.oauth.error.generic":
    "Algo salió mal durante el inicio de sesión. Por favor, intenta de nuevo.",
  "auth.oauth.backToLogin": "Volver al inicio de sesión",

  "common.timezones.america.anchorage": "Hora de Alaska - Anchorage",
  "common.timezones.america.losAngeles": "Hora del Pacífico - Los Angeles",
  "common.timezones.america.vancouver": "Hora del Pacífico - Vancouver",
  "common.timezones.america.denver": "Hora de la Montaña - Denver",
  "common.timezones.america.phoenix": "Hora de la Montaña - Phoenix",
  "common.timezones.america.chicago": "Hora Central - Chicago",
  "common.timezones.america.mexicoCity": "Hora Central - Ciudad de México",
  "common.timezones.america.newYork": "Hora del Este - New York",
  "common.timezones.america.toronto": "Hora del Este - Toronto",
  "common.timezones.america.bogota": "Hora de Colombia - Bogotá",
  "common.timezones.america.lima": "Hora de Perú - Lima",
  "common.timezones.america.caracas": "Hora de Venezuela - Caracas",
  "common.timezones.america.santiago": "Hora de Chile - Santiago",
  "common.timezones.america.saoPaulo": "Hora de Brasilia - São Paulo",
  "common.timezones.america.buenosAires": "Hora de Argentina - Buenos Aires",

  "common.timezones.atlantic.reykjavik": "Hora de Islandia - Reykjavik",
  "common.timezones.europe.london": "Hora del Reino Unido - London",
  "common.timezones.europe.dublin": "Hora de Irlanda - Dublín",
  "common.timezones.europe.lisbon": "Hora de Portugal - Lisboa",
  "common.timezones.europe.paris": "Hora de Europa Central - París",
  "common.timezones.europe.berlin": "Hora de Europa Central - Berlín",
  "common.timezones.europe.madrid": "Hora de Europa Central - Madrid",
  "common.timezones.europe.rome": "Hora de Europa Central - Roma",
  "common.timezones.europe.amsterdam": "Hora de Europa Central - Amsterdam",
  "common.timezones.europe.brussels": "Hora de Europa Central - Bruselas",
  "common.timezones.europe.stockholm": "Hora de Europa Central - Estocolmo",
  "common.timezones.europe.warsaw": "Hora de Europa Central - Varsovia",
  "common.timezones.europe.athens": "Hora de Europa del Este - Atenas",
  "common.timezones.europe.helsinki": "Hora de Europa del Este - Helsinki",
  "common.timezones.europe.istanbul": "Hora de Turquía - Estambul",
  "common.timezones.europe.moscow": "Hora de Moscú - Moscú",

  "common.timezones.africa.cairo": "Hora de Egipto - El Cairo",
  "common.timezones.africa.johannesburg": "Hora de Sudáfrica - Johannesburg",
  "common.timezones.asia.jerusalem": "Hora de Israel - Jerusalén",
  "common.timezones.asia.dubai": "Hora del Golfo - Dubai",

  "common.timezones.asia.kolkata": "Hora de India - Kolkata",
  "common.timezones.asia.dhaka": "Hora de Bangladesh - Dhaka",
  "common.timezones.asia.bangkok": "Hora de Indochina - Bangkok",
  "common.timezones.asia.jakarta": "Hora de Indonesia - Jakarta",
  "common.timezones.asia.shanghai": "Hora de China - Shanghai",
  "common.timezones.asia.hongKong": "Hora de Hong Kong",
  "common.timezones.asia.singapore": "Hora de Singapur",
  "common.timezones.asia.taipei": "Hora de Taiwán - Taipei",
  "common.timezones.asia.seoul": "Hora de Corea - Seoul",
  "common.timezones.asia.tokyo": "Hora de Japón - Tokyo",

  "common.timezones.australia.perth": "Australia Occidental - Perth",
  "common.timezones.australia.adelaide": "Australia Central - Adelaide",
  "common.timezones.australia.brisbane": "Australia Oriental - Brisbane",
  "common.timezones.australia.sydney": "Australia Oriental - Sydney",
  "common.timezones.australia.melbourne": "Australia Oriental - Melbourne",
  "common.timezones.pacific.auckland": "Hora de Nueva Zelanda - Auckland",

  "common.timezones.utc": "Tiempo Universal Coordinado (UTC)",

  // Schema-driven form (PLA-676): widgets + DefaultValuePanel.
  "featureConfig.form.schemaForm.defaults": "Valores predeterminados",
  "featureConfig.form.schemaForm.defaultsEmpty": "(no hay campos editables)",
  "featureConfig.form.schemaForm.defaultsEmptyList": "(lista vacía)",
  "featureConfig.form.schemaForm.defaultsEmptyMap": "(mapa vacío)",
  "featureConfig.form.schemaForm.noDefault": "(sin valor predeterminado)",
  "featureConfig.form.schemaForm.placeholder.email": "correo electrónico",
  "featureConfig.form.schemaForm.placeholder.url": "https://...",
  "featureConfig.form.schemaForm.placeholder.regex":
    "debe coincidir con el patrón",
  "featureConfig.form.schemaForm.list.add": "Agregar",
  "featureConfig.form.schemaForm.list.remove": "Quitar",
  "featureConfig.form.schemaForm.object.add": "Agregar sección",
  "featureConfig.form.schemaForm.object.remove": "Quitar sección",
  "featureConfig.form.schemaForm.record.add": "Agregar fila",
  "featureConfig.form.schemaForm.record.remove": "Quitar",
  "featureConfig.form.schemaForm.record.keyPlaceholder": "clave",
  "featureConfig.form.schemaForm.record.valuePlaceholder": "valor",
  "featureConfig.form.schemaForm.record.duplicateKeyError":
    "La clave ya existe — elige una diferente",

  "participantProfile.topPicks.overrideBadge": "Sugerencia del anfitrión",
  "participantProfile.aiSummary": "La lectura de Porter.",
  "participantProfile.linkedin.title": "Resumen de LinkedIn",
  "participantProfile.linkedin.notAvailable": "Todavía no hay nada de LinkedIn",
  "participantProfile.linkedin.notAvailableDescription":
    "Cuando agreguen su LinkedIn, lo que escribieron sobre sí mismos aparece acá.",
  "participantProfile.linkedin.notAvailableDescriptionOwn":
    "Cuando agregues tu LinkedIn, lo que escribiste sobre ti aparece aquí.",
  "participantProfile.photo.enlarge": "Ver foto completa",
  "participantProfile.photo.closePreview": "Cerrar vista previa de foto",
} as const satisfies TranslationDictionaryType;

// Compile-time assertion: Spanish must have same keys as English
export type AssertEsMatchesEnType = AssertMatchingPlaceholders<
  typeof en,
  typeof es
>;
